import React, { useEffect, useMemo, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  Image,
  RefreshControl,
  TextInput,
  Platform,
} from 'react-native';
import { useNavigate } from 'react-router-dom';
import { Ionicons } from '@expo/vector-icons';
import { DynamicTitle } from '../DynamicTitle';
import {
  clearComensalReadCache,
  fetchEdicionActivaCached,
  getPropuestasByEdicionIdCached,
  type Propuesta,
} from '../../lib/cache/comensalReadCache';
import { rankPropuestasPorDistancia, type PropuestaConDistancia } from '../../lib/utils/propuestaDistancia';
import { getCurrentUserPosition } from '../../lib/utils/userGeolocation';
import { getImageDisplayUrl } from '../../lib/utils/imageTransform';
import { LazyStorageImage } from '../LazyStorageImage';
import { marcarVotarDesdeAppShell } from '../../lib/catalogoEdicionFichaStorage';
import { urlFotoMostrar } from '../../lib/utils/propuestaFotos';
import { COMENSAL_TAB_BAR_OVERLAY_INSET } from './ComensalBottomTabBar';
import { ComensalQrVotacionScanner } from './ComensalQrVotacionScanner';

/** Valor por defecto del radio (metros) si el campo queda vacío o no es válido. */
export const RADIO_VOTAR_METROS_DEFAULT = 5000;

/** Normaliza metros: entero ≥ 1 (sin límite superior). */
function normalizeRadioMetros(value: number): number {
  const n = Math.round(value);
  if (Number.isNaN(n) || n < 1) return RADIO_VOTAR_METROS_DEFAULT;
  return n;
}

function formatUbicacionActual(lat: number, lng: number, accuracyM: number | null): string {
  const la = lat.toFixed(6);
  const lo = lng.toFixed(6);
  if (accuracyM != null && !Number.isNaN(accuracyM) && accuracyM > 0) {
    return `${la}, ${lo}\nPrecisión aprox.: ±${Math.round(accuracyM)} m`;
  }
  return `${la}, ${lo}`;
}

function formatDistanciaKmEs(km: number): string {
  if (km < 1) {
    const m = Math.round(km * 1000);
    return `${m} m`;
  }
  return `${km.toLocaleString('es-AR', { maximumFractionDigits: 1, minimumFractionDigits: km < 10 ? 1 : 0 })} km`;
}

function propuestaListaImagenUrl(p: Propuesta): string | null {
  const fotos = p.fotos;
  const idx = p.foto_seleccionada_index ?? 0;
  if (fotos?.length) {
    const foto = fotos[idx] ?? fotos[0];
    if (foto?.url) return urlFotoMostrar(foto);
  }
  if (p.foto_restaurante?.trim()) return p.foto_restaurante.trim();
  return null;
}

export function ComensalVotarPage() {
  const navigate = useNavigate();
  /** `undefined` = aún cargando; `null` = sin edición activa */
  const [edicionId, setEdicionId] = useState<number | null | undefined>(undefined);
  const [edicionNombre, setEdicionNombre] = useState<string | null>(null);
  const [mapCoords, setMapCoords] = useState<{
    lat: number;
    lng: number;
    accuracyM: number | null;
  } | null>(null);
  const [mapCoordsReady, setMapCoordsReady] = useState(false);
  /** Lista completa ordenada por distancia; el filtro por metros es en cliente */
  const [rankedCache, setRankedCache] = useState<PropuestaConDistancia[] | null>(null);
  const [listLoading, setListLoading] = useState(false);
  const [radioMetros, setRadioMetros] = useState(RADIO_VOTAR_METROS_DEFAULT);
  const [radioInput, setRadioInput] = useState(String(RADIO_VOTAR_METROS_DEFAULT));
  const [refreshing, setRefreshing] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [qrScannerVisible, setQrScannerVisible] = useState(false);
  /** Incrementar tras pull-to-refresh para forzar nueva lectura aunque edición/GPS no cambien. */
  const [dataRefreshKey, setDataRefreshKey] = useState(0);

  const items = useMemo(() => {
    if (!rankedCache) return [];
    const maxKm = radioMetros / 1000;
    return rankedCache.filter((r) => r.distanceKm <= maxKm);
  }, [rankedCache, radioMetros]);

  const commitRadioInput = () => {
    const raw = radioInput.replace(/\D/g, '');
    const parsed = raw === '' ? RADIO_VOTAR_METROS_DEFAULT : parseInt(raw, 10);
    const c = normalizeRadioMetros(parsed);
    setRadioMetros(c);
    setRadioInput(String(c));
  };

  useEffect(() => {
    let cancelled = false;
    setLoadError(null);
    fetchEdicionActivaCached()
      .then(({ id, banner }) => {
        if (cancelled) return;
        setEdicionId(id);
        setEdicionNombre(banner?.nombre ?? (id != null ? 'Edición activa' : null));
      })
      .catch(() => {
        if (!cancelled) {
          setEdicionId(null);
          setLoadError('No se pudo cargar la edición');
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setMapCoordsReady(false);
    getCurrentUserPosition()
      .then((p) => {
        if (!cancelled)
          setMapCoords({
            lat: p.latitude,
            lng: p.longitude,
            accuracyM: p.accuracy ?? null,
          });
      })
      .catch(() => {
        if (!cancelled) setMapCoords(null);
      })
      .finally(() => {
        if (!cancelled) setMapCoordsReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (edicionId == null || edicionId === undefined || !mapCoordsReady) return;
    if (!mapCoords) {
      setRankedCache(null);
      return;
    }
    let cancelled = false;
    setRankedCache(null);
    setListLoading(true);
    setLoadError(null);
    (async () => {
      try {
        const list = await getPropuestasByEdicionIdCached(String(edicionId));
        const ranked = rankPropuestasPorDistancia(list, mapCoords.lat, mapCoords.lng);
        if (!cancelled) setRankedCache(ranked);
      } catch {
        if (!cancelled) {
          setLoadError('No se pudieron cargar las propuestas');
          setRankedCache([]);
        }
      } finally {
        if (!cancelled) setListLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [edicionId, mapCoords, mapCoordsReady, dataRefreshKey]);

  const onRefresh = async () => {
    setRefreshing(true);
    setLoadError(null);
    try {
      clearComensalReadCache();
      const [{ id, banner }, posRaw] = await Promise.all([
        fetchEdicionActivaCached(),
        getCurrentUserPosition().then(
          (p) => ({
            lat: p.latitude,
            lng: p.longitude,
            accuracyM: p.accuracy ?? null,
          }),
          () => null
        ),
      ]);
      setEdicionId(id);
      setEdicionNombre(banner?.nombre ?? (id != null ? 'Edición activa' : null));
      setMapCoords(posRaw);
      if (id == null || !posRaw) setRankedCache(null);
      else setDataRefreshKey((k) => k + 1);
    } catch {
      setLoadError('Error al actualizar');
    } finally {
      setRefreshing(false);
    }
  };

  if (edicionId === undefined) {
    return (
      <View style={styles.centered}>
        <DynamicTitle title="Votar — As Gastronómico" />
        <ActivityIndicator size="large" color="#030213" />
        <Text style={styles.muted}>Cargando…</Text>
      </View>
    );
  }

  if (edicionId === null) {
    return (
      <View style={styles.screen}>
        <DynamicTitle title="Votar — As Gastronómico" />
        <View style={styles.card}>
          <Ionicons name="calendar-outline" size={40} color="#9ca3af" />
          <Text style={styles.cardTitle}>No hay edición activa</Text>
          <Text style={styles.cardBody}>
            Cuando haya una edición en curso, verás aquí las propuestas cercanas a ti para votar.
          </Text>
          {loadError ? <Text style={styles.error}>{loadError}</Text> : null}
          <TouchableOpacity
            style={styles.qrScanBtn}
            onPress={() => setQrScannerVisible(true)}
            activeOpacity={0.88}
          >
            <Ionicons name="qr-code-outline" size={22} color="#030213" />
            <Text style={styles.qrScanBtnText}>Escanear QR</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.primaryBtn} onPress={() => navigate('/')} activeOpacity={0.88}>
            <Text style={styles.primaryBtnText}>Ir al inicio</Text>
          </TouchableOpacity>
        </View>
        <ComensalQrVotacionScanner
          visible={qrScannerVisible}
          onClose={() => setQrScannerVisible(false)}
          onPropuestaId={(id) => {
            marcarVotarDesdeAppShell();
            navigate(`/votar/${id}`);
          }}
          onRestauranteCodigoQr={(c) => navigate(`/restaurante/${encodeURIComponent(c)}`)}
        />
      </View>
    );
  }

  return (
    <View style={styles.shell}>
      <DynamicTitle title="Votar — As Gastronómico" />
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={[styles.scrollContent, { paddingBottom: COMENSAL_TAB_BAR_OVERLAY_INSET + 16 }]}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
        keyboardShouldPersistTaps="handled"
      >
        <Text style={styles.headline}>Votar cerca de ti</Text>
        {edicionNombre ? <Text style={styles.subhead}>{edicionNombre}</Text> : null}

        <TouchableOpacity
          style={styles.qrScanBtn}
          onPress={() => setQrScannerVisible(true)}
          activeOpacity={0.88}
        >
          <Ionicons name="qr-code-outline" size={22} color="#030213" />
          <Text style={styles.qrScanBtnText}>Escanear QR</Text>
        </TouchableOpacity>

        {!mapCoordsReady ? (
          <View style={styles.rowLoad}>
            <ActivityIndicator size="small" color="#030213" />
            <Text style={styles.muted}>Obteniendo tu ubicación…</Text>
          </View>
        ) : !mapCoords ? (
          <View style={styles.warnBox}>
            <Ionicons name="location-off-outline" size={22} color="#92400e" />
            <Text style={styles.warnText}>
              Activa el permiso de ubicación para ver qué propuestas hay a tu alrededor.
            </Text>
          </View>
        ) : (
          <>
            <Text style={styles.fieldLabel}>Ubicación actual</Text>
            <View style={styles.fieldReadonly}>
              <Text style={styles.fieldReadonlyText} selectable>
                {formatUbicacionActual(mapCoords.lat, mapCoords.lng, mapCoords.accuracyM)}
              </Text>
            </View>

            <Text style={styles.fieldLabel}>Radio de búsqueda (metros)</Text>
            <TextInput
              style={styles.fieldInput}
              value={radioInput}
              onChangeText={setRadioInput}
              onBlur={commitRadioInput}
              onSubmitEditing={commitRadioInput}
              keyboardType="number-pad"
              placeholder={String(RADIO_VOTAR_METROS_DEFAULT)}
              placeholderTextColor="#9ca3af"
              returnKeyType="done"
              accessibilityLabel="Metros a la redonda desde tu ubicación"
            />

            <View style={styles.chipRow}>
              <Ionicons name="locate-outline" size={18} color="#030213" />
              <Text style={styles.chipText}>
                Propuestas a no más de <Text style={styles.chipStrong}>{radioMetros} m</Text> de tu
                posición
              </Text>
            </View>
          </>
        )}

        {mapCoordsReady && mapCoords && (listLoading || rankedCache === null) ? (
          <View style={styles.rowLoad}>
            <ActivityIndicator size="small" color="#030213" />
            <Text style={styles.muted}>Cargando propuestas…</Text>
          </View>
        ) : null}

        {mapCoordsReady &&
        mapCoords &&
        !listLoading &&
        rankedCache !== null &&
        items.length === 0 ? (
          <View style={styles.emptyBox}>
            <Ionicons name="map-outline" size={36} color="#9ca3af" />
            <Text style={styles.emptyTitle}>Nada en este radio</Text>
            <Text style={styles.emptyBody}>
              No hay propuestas con ubicación conocida dentro de {radioMetros} m de donde estás. Prueba a ampliar el
              radio o revisa el catálogo completo.
            </Text>
            <TouchableOpacity
              style={styles.secondaryBtn}
              onPress={() => navigate(`/edicion/${edicionId}`)}
              activeOpacity={0.88}
            >
              <Text style={styles.secondaryBtnText}>Ver todas las propuestas</Text>
            </TouchableOpacity>
          </View>
        ) : mapCoordsReady &&
          mapCoords &&
          !listLoading &&
          rankedCache !== null &&
          items.length > 0 ? (
          <>
            <Text style={styles.countLabel}>
              {items.length} {items.length === 1 ? 'propuesta' : 'propuestas'} en tu zona
            </Text>
            {items.map(({ propuesta, distanceKm, ubicacionCorta }) => {
              const img = propuestaListaImagenUrl(propuesta);
              const uri = img
                ? getImageDisplayUrl(img, { preset: 'listThumb' }) || img.replace(/^http:/, 'https:')
                : null;
              const restNombre =
                propuesta.restaurante?.nombre_mostrar || propuesta.restaurante?.nombre_oficial || '';
              return (
                <View key={propuesta.id} style={styles.rowCard}>
                  {uri ? (
                    <LazyStorageImage uri={uri} style={styles.thumb} resizeMode="cover" />
                  ) : (
                    <View style={[styles.thumb, styles.thumbPlaceholder]}>
                      <Ionicons name="image-outline" size={28} color="#9ca3af" />
                    </View>
                  )}
                  <View style={styles.rowBody}>
                    <Text style={styles.rowTitle} numberOfLines={2}>
                      {propuesta.titulo}
                    </Text>
                    {restNombre ? (
                      <Text style={styles.rowRest} numberOfLines={1}>
                        {restNombre}
                      </Text>
                    ) : null}
                    <Text style={styles.rowUbic} numberOfLines={1}>
                      {ubicacionCorta}
                    </Text>
                    <Text style={styles.rowDist}>{formatDistanciaKmEs(distanceKm)}</Text>
                    <TouchableOpacity
                      style={styles.voteBtn}
                      onPress={() => {
                        marcarVotarDesdeAppShell();
                        navigate(`/votar/${propuesta.id}`);
                      }}
                      activeOpacity={0.88}
                    >
                      <Ionicons name="checkbox-outline" size={18} color="#ffffff" />
                      <Text style={styles.voteBtnText}>Ir a votar</Text>
                    </TouchableOpacity>
                  </View>
                </View>
              );
            })}
            <TouchableOpacity
              style={styles.linkWrap}
              onPress={() => navigate(`/edicion/${edicionId}`)}
              activeOpacity={0.7}
            >
              <Text style={styles.linkText}>Ver catálogo completo de la edición</Text>
              <Ionicons name="chevron-forward" size={18} color="#030213" />
            </TouchableOpacity>
          </>
        ) : null}

        {loadError ? <Text style={styles.errorMargin}>{loadError}</Text> : null}
      </ScrollView>
      <ComensalQrVotacionScanner
        visible={qrScannerVisible}
        onClose={() => setQrScannerVisible(false)}
        onPropuestaId={(id) => {
          marcarVotarDesdeAppShell();
          navigate(`/votar/${id}`);
        }}
        onRestauranteCodigoQr={(c) => navigate(`/restaurante/${encodeURIComponent(c)}`)}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  shell: {
    flex: 1,
    backgroundColor: '#f9fafb',
  },
  scroll: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: 20,
    paddingTop: 8,
  },
  centered: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f9fafb',
    gap: 12,
  },
  muted: {
    fontSize: 14,
    color: '#6b7280',
  },
  screen: {
    flex: 1,
    backgroundColor: '#f9fafb',
    paddingHorizontal: 24,
    paddingTop: 24,
    justifyContent: 'center',
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    padding: 24,
    alignItems: 'center',
    gap: 12,
  },
  cardTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#030213',
    textAlign: 'center',
  },
  cardBody: {
    fontSize: 15,
    color: '#6b7280',
    textAlign: 'center',
    lineHeight: 22,
  },
  error: {
    fontSize: 13,
    color: '#dc2626',
    textAlign: 'center',
  },
  errorMargin: {
    marginTop: 16,
    fontSize: 13,
    color: '#dc2626',
    textAlign: 'center',
  },
  primaryBtn: {
    marginTop: 8,
    backgroundColor: '#030213',
    paddingVertical: 14,
    paddingHorizontal: 24,
    borderRadius: 12,
    alignSelf: 'stretch',
  },
  primaryBtnText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '600',
    textAlign: 'center',
  },
  headline: {
    fontSize: 22,
    fontWeight: '800',
    color: '#030213',
    marginBottom: 4,
  },
  subhead: {
    fontSize: 15,
    color: '#6b7280',
    marginBottom: 12,
  },
  qrScanBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    backgroundColor: '#ffffff',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#030213',
    paddingVertical: 14,
    paddingHorizontal: 18,
    marginBottom: 16,
  },
  qrScanBtnText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#030213',
  },
  fieldLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: '#374151',
    marginBottom: 6,
    marginTop: 2,
  },
  fieldReadonly: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    paddingHorizontal: 14,
    paddingVertical: 12,
    marginBottom: 14,
  },
  fieldReadonlyText: {
    fontSize: 14,
    color: '#030213',
    fontFamily: Platform.select({ ios: 'Menlo', android: 'monospace', default: 'monospace' }),
    lineHeight: 20,
  },
  fieldInput: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    paddingHorizontal: 14,
    paddingVertical: 12,
    fontSize: 16,
    color: '#030213',
    marginBottom: 4,
  },
  chipRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
    backgroundColor: '#ffffff',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    padding: 12,
    marginBottom: 16,
  },
  chipText: {
    flex: 1,
    fontSize: 14,
    color: '#4b5563',
    lineHeight: 20,
  },
  chipStrong: {
    fontWeight: '700',
    color: '#030213',
  },
  rowLoad: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginBottom: 16,
  },
  warnBox: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
    backgroundColor: '#fffbeb',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#fde68a',
    padding: 14,
    marginBottom: 16,
  },
  warnText: {
    flex: 1,
    fontSize: 14,
    color: '#92400e',
    lineHeight: 20,
  },
  emptyBox: {
    alignItems: 'center',
    paddingVertical: 24,
    paddingHorizontal: 8,
    gap: 10,
  },
  emptyTitle: {
    fontSize: 17,
    fontWeight: '700',
    color: '#030213',
  },
  emptyBody: {
    fontSize: 14,
    color: '#6b7280',
    textAlign: 'center',
    lineHeight: 21,
  },
  secondaryBtn: {
    marginTop: 8,
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#030213',
    backgroundColor: '#ffffff',
  },
  secondaryBtnText: {
    fontSize: 15,
    fontWeight: '600',
    color: '#030213',
  },
  countLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#030213',
    marginBottom: 12,
  },
  rowCard: {
    flexDirection: 'row',
    backgroundColor: '#ffffff',
    borderRadius: 14,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    overflow: 'hidden',
    marginBottom: 12,
  },
  thumb: {
    width: 108,
    minHeight: 120,
    backgroundColor: '#f3f4f6',
  },
  thumbPlaceholder: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  rowBody: {
    flex: 1,
    padding: 12,
    minWidth: 0,
  },
  rowTitle: {
    fontSize: 15,
    fontWeight: '700',
    color: '#030213',
    lineHeight: 20,
  },
  rowRest: {
    fontSize: 13,
    color: '#6b7280',
    marginTop: 4,
  },
  rowUbic: {
    fontSize: 12,
    color: '#4b5563',
    marginTop: 6,
    fontWeight: '500',
  },
  rowDist: {
    fontSize: 13,
    fontWeight: '600',
    color: '#030213',
    marginTop: 4,
  },
  voteBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    marginTop: 10,
    backgroundColor: '#030213',
    paddingVertical: 10,
    borderRadius: 10,
  },
  voteBtnText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '600',
  },
  linkWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    marginTop: 8,
    marginBottom: 8,
    paddingVertical: 12,
  },
  linkText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#030213',
  },
});
